import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { BuffOrDebuff, WarbandMemberDTO } from '../../types';
import { PopoverState } from '../../hooks/useItemPopover';
import { formatBuffEffect, getBuffName, getBuffSourceDescription } from '../../utils/formatting';
import { ItemDetailsPopover } from './ItemDetailsPopover';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ManageBuffsDebuffsModal } from '../modals/ManageBuffsDebuffsModal';
import { editButtonOffsetSx } from '../../App';

interface MemberEffectsProps {
  member: WarbandMemberDTO;
  memberRole: 'wizard' | 'apprentice' | 'soldier';
  editMode: boolean;
  isDefeated: boolean;
  onUpdateMemberBuffs: (buffIds: string[]) => void;
}

export const MemberEffects: React.FC<MemberEffectsProps> = ({
  member,
  memberRole,
  editMode,
  isDefeated,
  onUpdateMemberBuffs,
}) => {
  const [manageBuffsOpen, setManageBuffsOpen] = useState(false);
  const [buffPendingDelete, setBuffPendingDelete] = useState<BuffOrDebuff | null>(null);
  const [buffStackChooseOpen, setBuffStackChooseOpen] = useState(false);
  const [buffPopover, setBuffPopover] = useState<PopoverState | null>(null);

  const sortedBuffs = [...(member.buffsAndDebuffs ?? [])].sort((a, b) => {
    if (a.source === 'spell' && b.source !== 'spell') return 1;
    if (a.source !== 'spell' && b.source === 'spell') return -1;
    return getBuffName(a).localeCompare(getBuffName(b));
  });

  const handleBuffChipClick = async (e: React.MouseEvent<HTMLElement>, buff: BuffOrDebuff) => {
    setBuffPopover({
      anchor: e.currentTarget,
      name: getBuffName(buff),
      description: 'Loading…',
      modifiers: formatBuffEffect(buff),
      tags: [],
    });
    const desc = await getBuffSourceDescription(member.warbandId, buff);
    setBuffPopover((prev) => prev ? { ...prev, description: desc || null } : null);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Effects
        </Typography>
        <IconButton
          sx={editButtonOffsetSx}
          disabled={isDefeated}
          size="small"
          onClick={() => setManageBuffsOpen(true)}
        >
          <AddIcon />
        </IconButton>
      </Stack>
      {sortedBuffs.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {sortedBuffs.map((buffOrDebuff) => {
            const count = buffOrDebuff.stackCount ?? 1;
            const chipLabel = count > 1 ? `${getBuffName(buffOrDebuff)} ×${count}` : getBuffName(buffOrDebuff);
            return (
              <Chip
                key={buffOrDebuff.id}
                size="small"
                label={chipLabel}
                onClick={(e) => handleBuffChipClick(e, buffOrDebuff)}
                onDelete={
                  (buffOrDebuff.source === 'spell' || (buffOrDebuff.source === 'injury' && editMode && memberRole !== 'soldier'))
                    ? () => {
                      setBuffPendingDelete(buffOrDebuff);
                      if (count > 1) setBuffStackChooseOpen(true);
                    }
                    : undefined
                }
              />
            );
          })}
        </Box>
      )}

      <ManageBuffsDebuffsModal
        open={manageBuffsOpen}
        editMode={editMode}
        member={member}
        memberRole={memberRole}
        onClose={() => setManageBuffsOpen(false)}
      />

      <ItemDetailsPopover
        popover={buffPopover}
        onClose={() => setBuffPopover(null)}
      />

      {/* Single-buff remove confirmation */}
      <ConfirmActionDialog
        open={buffPendingDelete !== null && !buffStackChooseOpen}
        title="Remove buff / debuff"
        message={buffPendingDelete ? `Remove "${getBuffName(buffPendingDelete)}" from ${member.name}?` : ''}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setBuffPendingDelete(null)}
        onConfirm={() => {
          if (!buffPendingDelete) return;
          const remaining = (member.buffsAndDebuffs ?? [])
            .filter((b) => b.id !== buffPendingDelete.id)
            .flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
          onUpdateMemberBuffs(remaining);
          setBuffPendingDelete(null);
        }}
      />

      {/* Multi-stack member buff removal */}
      <Dialog
        open={buffStackChooseOpen}
        onClose={() => { setBuffPendingDelete(null); setBuffStackChooseOpen(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove buff / debuff</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {buffPendingDelete
              ? `"${getBuffName(buffPendingDelete)}" has ${buffPendingDelete.stackCount ?? 1} stacks on ${member.name}. How many would you like to remove?`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBuffPendingDelete(null); setBuffStackChooseOpen(false); }}>Cancel</Button>
          <Button
            color="warning"
            onClick={() => {
              if (!buffPendingDelete) return;
              const allIds = (member.buffsAndDebuffs ?? []).flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
              const idx = allIds.lastIndexOf(buffPendingDelete.id);
              const remaining = [...allIds.slice(0, idx), ...allIds.slice(idx + 1)];
              onUpdateMemberBuffs(remaining);
              setBuffPendingDelete(null);
              setBuffStackChooseOpen(false);
            }}
          >
            Remove one stack
          </Button>
          <Button
            color="error"
            onClick={() => {
              if (!buffPendingDelete) return;
              const remaining = (member.buffsAndDebuffs ?? [])
                .filter((b) => b.id !== buffPendingDelete.id)
                .flatMap((b) => Array(b.stackCount ?? 1).fill(b.id));
              onUpdateMemberBuffs(remaining);
              setBuffPendingDelete(null);
              setBuffStackChooseOpen(false);
            }}
          >
            Remove all stacks
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
